import React, { useState, useCallback } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { AlertDialog } from './AlertDialog';
import { PromptDialog } from './PromptDialog';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
}

interface AlertOptions {
  title: string;
  message: string;
  confirmText?: string;
  type?: 'info' | 'warning' | 'error' | 'success';
}

interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'warning';
  defaultValue?: string;
}

export const useDialog = () => {
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions | null;
    resolve: ((value: boolean) => void) | null;
  }>({ isOpen: false, options: null, resolve: null });

  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    options: AlertOptions | null;
    resolve: (() => void) | null;
  }>({ isOpen: false, options: null, resolve: null });

  const [promptState, setPromptState] = useState<{
    isOpen: boolean;
    options: PromptOptions | null;
    resolve: ((value: string | null) => void) | null;
  }>({ isOpen: false, options: null, resolve: null });

  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        options,
        resolve
      });
    });
  }, []);

  const showAlert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        options,
        resolve
      });
    });
  }, []);

  const showPrompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setPromptState({
        isOpen: true,
        options,
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(true);
    }
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  }, [confirmState.resolve]);

  const handleConfirmCancel = useCallback(() => {
    if (confirmState.resolve) {
      confirmState.resolve(false);
    }
    setConfirmState(prev => ({ ...prev, isOpen: false }));
  }, [confirmState.resolve]);

  const handleAlertConfirm = useCallback(() => {
    if (alertState.resolve) {
      alertState.resolve();
    }
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, [alertState.resolve]);

  const handlePromptConfirm = useCallback((value: string) => {
    if (promptState.resolve) {
      promptState.resolve(value);
    }
    setPromptState(prev => ({ ...prev, isOpen: false }));
  }, [promptState.resolve]);

  const handlePromptCancel = useCallback(() => {
    if (promptState.resolve) {
      promptState.resolve(null);
    }
    setPromptState(prev => ({ ...prev, isOpen: false }));
  }, [promptState.resolve]);

  const DialogComponents = (
    <>
      {confirmState.isOpen && confirmState.options && (
        <ConfirmDialog
          isOpen={confirmState.isOpen}
          title={confirmState.options.title}
          message={confirmState.options.message}
          confirmText={confirmState.options.confirmText}
          cancelText={confirmState.options.cancelText}
          type={confirmState.options.type}
          onConfirm={handleConfirm}
          onCancel={handleConfirmCancel}
        />
      )}
      {alertState.isOpen && alertState.options && (
        <AlertDialog
          isOpen={alertState.isOpen}
          title={alertState.options.title}
          message={alertState.options.message}
          confirmText={alertState.options.confirmText}
          type={alertState.options.type}
          onConfirm={handleAlertConfirm}
        />
      )}
      {promptState.isOpen && promptState.options && (
        <PromptDialog
          isOpen={promptState.isOpen}
          title={promptState.options.title}
          message={promptState.options.message}
          placeholder={promptState.options.placeholder}
          confirmText={promptState.options.confirmText}
          cancelText={promptState.options.cancelText}
          type={promptState.options.type}
          defaultValue={promptState.options.defaultValue}
          onConfirm={handlePromptConfirm}
          onCancel={handlePromptCancel}
        />
      )}
    </>
  );

  return {
    showConfirm,
    showAlert,
    showPrompt,
    DialogComponents
  };
};

export default useDialog;
